import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X } from "lucide-react";

/**
 * Post-cutover Welcome Banner + First-Time Checklist.
 *
 * Only renders when the backend banner endpoint returns `active: true`.
 * Silently no-ops for non-owners or when no cutover has happened yet.
 */
export default function ProductionWelcomeBanner() {
  const [banner, setBanner] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [expanded, setExpanded] = useState(true);

  const load = async () => {
    try {
      const b = await api.get("/admin/production-clean-start/banner");
      if (!b.data?.active) { setBanner(null); return; }
      setBanner(b.data);
      const c = await api.get("/admin/production-clean-start/checklist");
      setChecklist(c.data?.items || []);
    } catch { setBanner(null); }
  };

  useEffect(() => { load(); }, []);

  const dismiss = async () => {
    try { await api.post("/admin/production-clean-start/banner/dismiss"); }
    catch { /* non-owner: silently ignore */ }
    setBanner(null);
  };

  if (!banner) return null;
  const doneCount = checklist.filter(c => c.done).length;

  return (
    <div className="border border-emerald-500/50 bg-emerald-500/5 p-3 space-y-2"
         data-testid="production-welcome-banner">
      <div className="flex items-center gap-3">
        <div className="text-[10px] mono uppercase tracking-widest text-emerald-500">
          Production initialized · {banner.environment || "—"}
          {banner.application_version && ` · v${banner.application_version}`}
        </div>
        <button onClick={() => setExpanded(e => !e)}
                className="ml-auto text-[10px] mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
                data-testid="welcome-banner-toggle">
          {expanded ? "Collapse" : `Expand (${doneCount}/${checklist.length})`}
        </button>
        <button onClick={dismiss}
                className="text-muted-foreground hover:text-foreground"
                data-testid="welcome-banner-dismiss"
                aria-label="Dismiss">
          <X size={14} strokeWidth={1.75}/>
        </button>
      </div>
      <div className="text-xs text-muted-foreground">
        Your clean-start migration completed. Complete the checklist below before enabling live trading.
      </div>
      {expanded && checklist.length > 0 && (
        <ol className="text-[11px] space-y-0.5 mt-1" data-testid="welcome-banner-checklist">
          {checklist.map((c, i) => (
            <li key={c.key} className="flex items-center gap-2">
              <span className={c.done ? "text-emerald-500" : "text-muted-foreground"}>
                {c.done ? "✓" : `${i + 1}.`}
              </span>
              <span className={c.done ? "line-through text-muted-foreground" : ""}>
                {c.label}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
